import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateHelpRequest, useHelpRequestAllowance } from "@/hooks/useHelpRequests";
import { useMentorTopics } from "@/hooks/useMentorTopics";
import { toast } from "sonner";
import { AlertCircle, Hand, Info } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface CreateHelpRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  courseId: string;
  moduleId?: string | null;
  moduleName?: string;
}

export function CreateHelpRequestDialog({
  open,
  onOpenChange,
  courseId,
  moduleId,
  moduleName,
}: CreateHelpRequestDialogProps) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [selectedTopicId, setSelectedTopicId] = useState("");

  const { data: allowance, isLoading: allowanceLoading } = useHelpRequestAllowance();
  const { data: topics, isLoading: topicsLoading } = useMentorTopics();
  const createRequest = useCreateHelpRequest();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !content.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!selectedTopicId) {
      toast.error("Please select a topic for your request");
      return;
    }

    try {
      await createRequest.mutateAsync({
        course_id: courseId,
        module_id: moduleId,
        title: title.trim(),
        content: content.trim(),
        is_private: isPrivate,
        topic_id: selectedTopicId,
      });

      toast.success("Help request submitted! Mentors and instructors have been notified.");
      setTitle("");
      setContent("");
      setIsPrivate(false);
      setSelectedTopicId("");
      onOpenChange(false);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to submit help request";
      if (message.includes("row-level security")) {
        toast.error("You've reached your help request limit. Please wait before submitting another.");
      } else {
        toast.error(message);
      }
    }
  };

  const canSubmit = allowance?.can_request && !createRequest.isPending && !!selectedTopicId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hand className="h-5 w-5 text-primary" />
            Raise Your Hand
          </DialogTitle>
        </DialogHeader>

        {allowanceLoading || topicsLoading ? (
          <div className="py-4 text-center text-muted-foreground">Loading...</div>
        ) : !allowance?.can_request ? (
          <div className="py-4">
            <div className="flex items-start gap-3 rounded-lg border border-destructive/50 bg-destructive/10 p-4">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
              <div className="space-y-1">
                <p className="font-medium text-destructive">Request limit reached</p>
                <p className="text-sm text-muted-foreground">
                  You have {allowance?.remaining_this_week || 0} requests remaining this week.
                  {allowance?.cooldown_ends_at && (
                    <>
                      {" "}Your next request will be available{" "}
                      {formatDistanceToNow(new Date(allowance.cooldown_ends_at), { addSuffix: true })}.
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {moduleName && (
              <div className="text-sm text-muted-foreground">
                Requesting help for: <span className="font-medium text-foreground">{moduleName}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="topic">Topic *</Label>
              <Select value={selectedTopicId} onValueChange={setSelectedTopicId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a topic..." />
                </SelectTrigger>
                <SelectContent>
                  {topics?.map((topic) => (
                    <SelectItem key={topic.id} value={topic.id}>
                      {topic.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                Choose the topic that best describes the help you're seeking. Mentors review requests in their own time and are never required to respond.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="Brief summary of what you need help with"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={100}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Description *</Label>
              <Textarea
                id="content"
                placeholder="Describe your question or what you're struggling with in detail..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={4}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="private"
                checked={isPrivate}
                onCheckedChange={(checked) => setIsPrivate(checked === true)}
              />
              <Label htmlFor="private" className="text-sm font-normal cursor-pointer">
                Keep this request private (only visible to mentors, instructors, and admins)
              </Label>
            </div>

            <div className="text-xs text-muted-foreground">
              {allowance && (
                <>You have {allowance.remaining_this_week} help request{allowance.remaining_this_week !== 1 ? 's' : ''} remaining this week.</>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {createRequest.isPending ? "Submitting..." : "Submit Request"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
